import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Upload, CheckCircle, AlertCircle, Loader, ArrowLeft } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { apiFetch } from '../lib/api';

export default function PaymentProof() {
  const { orderNum } = useParams<{ orderNum: string }>();
  const navigate = useNavigate();

  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState('');
  const [uploading, setUploading] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (!selected) return;
    setFile(selected);
    setStatus('idle');
    const reader = new FileReader();
    reader.onload = (ev) => setPreview(ev.target?.result as string);
    reader.readAsDataURL(selected);
  };

  const handleUpload = async () => {
    if (!file || !orderNum) {
      setStatus('error');
      setMessage('請選擇檔案');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('orderNum', orderNum);

    try {
      const res = await apiFetch(`/public/orders/${orderNum}/payment-proof`, {
        method: 'POST',
        body: formData,
      });
      if (res.ok) {
        setStatus('success');
        setMessage('付款憑證上傳成功！我們會盡快確認。');
        setFile(null);
        setPreview('');
      } else {
        const json = await res.json().catch(() => ({}));
        setStatus('error');
        setMessage(json.error || '上傳失敗');
      }
    } catch {
      setStatus('error');
      setMessage('上傳過程中發生錯誤');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <section className="py-12 md:py-20">
        <div className="max-w-xl mx-auto px-4">
          <div className="text-center mb-8">
            <Upload size={56} className="mx-auto text-x1493-primary mb-4" />
            <h1 className="text-3xl font-bold">上傳付款憑證</h1>
            <p className="text-stone-600 mt-2">訂單編號 #{orderNum}</p>
          </div>

          {status === 'success' && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-5 mb-6 flex gap-3">
              <CheckCircle className="text-green-500 flex-shrink-0" size={28} />
              <div>
                <p className="font-bold text-xl text-green-700">上傳成功！</p>
                <p className="text-green-600">{message}</p>
                <button
                  onClick={() => navigate('/')}
                  className="mt-3 bg-green-600 text-white text-lg font-bold py-2 px-6 rounded-xl hover:bg-green-700"
                >
                  返回首頁
                </button>
              </div>
            </div>
          )}

          {status === 'error' && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-5 mb-6 flex gap-3">
              <AlertCircle className="text-red-500 flex-shrink-0" size={28} />
              <div>
                <p className="font-bold text-xl text-red-700">上傳失敗</p>
                <p className="text-red-600">{message}</p>
              </div>
            </div>
          )}

          {status !== 'success' && (
            <>
              <div className="bg-stone-50 rounded-2xl border-2 border-dashed border-stone-300 p-6 mb-6">
                {preview ? (
                  <div className="text-center">
                    <img src={preview} alt="Preview" className="max-h-48 mx-auto mb-4 rounded-xl" />
                    <p className="text-stone-600 mb-4 break-words">{file?.name}</p>
                    <button
                      onClick={() => { setFile(null); setPreview(''); }}
                      className="text-x1493-primary hover:text-red-700 font-medium"
                    >
                      更改檔案
                    </button>
                  </div>
                ) : (
                  <div className="text-center">
                    <Upload size={48} className="mx-auto text-stone-400 mb-3" />
                    <p className="text-xl font-bold text-stone-700 mb-2">點擊選擇或拖放檔案</p>
                    <p className="text-stone-500 mb-4">JPG、PNG（最大 5MB）</p>
                    <input type="file" accept="image/*" onChange={handleFileSelect} disabled={uploading} className="hidden" id="proof-file" />
                    <label htmlFor="proof-file" className="inline-block bg-x1493-primary text-white text-lg font-bold py-3 px-8 rounded-xl cursor-pointer hover:bg-red-700 transition">
                      選擇檔案
                    </label>
                  </div>
                )}
              </div>

              <div className="space-y-3">
                <button
                  onClick={handleUpload}
                  disabled={!file || uploading}
                  className="w-full bg-x1493-primary text-white text-lg font-bold py-4 rounded-xl hover:bg-red-700 disabled:bg-stone-300 transition flex items-center justify-center gap-2"
                >
                  {uploading && <Loader size={24} className="animate-spin" />}
                  上傳付款記錄
                </button>
                <button
                  onClick={() => navigate(-1)}
                  disabled={uploading}
                  className="w-full bg-stone-200 text-stone-800 text-lg font-bold py-4 rounded-xl hover:bg-stone-300 transition flex items-center justify-center gap-2"
                >
                  <ArrowLeft size={24} />
                  返回
                </button>
              </div>
            </>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
